import { useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import {
  Avatar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ludus/ui'
import type { Manager, Pipeline, PipelineId } from '@ludus/core'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import { pipelineAccent } from '@/shared/utils/pipelineAccent.ts'
import { useUpdateManager } from '../hooks/useUpdateManager.ts'
import { fullName, initialsOf } from '../utils/managerName.ts'
import { IdentityBadges } from './IdentityBadges.tsx'
import { saveErrorKey } from './saveError.ts'

const RISE_STEP_MS = 24
const RISE_MAX_STEPS = 12

interface ManagersTableProps {
  managers: Manager[]
  pipelines: Pipeline[]
}

export function ManagersTable({ managers, pipelines }: ManagersTableProps) {
  const { t } = useTranslation()
  return (
    <div className="bg-gradient-panel-elevated overflow-hidden rounded-xl border border-border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('pages.managers.table.manager')}</TableHead>
            {pipelines.map((pipeline) => (
              <TableHead key={pipeline.id} className="text-center">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: pipelineAccent(pipeline).hex }}
                    aria-hidden="true"
                  />
                  {pipeline.name}
                </span>
              </TableHead>
            ))}
            <TableHead>{t('pages.managers.table.extensions')}</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {managers.map((manager, index) => (
            <ManagerRow
              key={manager.id}
              manager={manager}
              pipelines={pipelines}
              riseDelayMs={Math.min(index, RISE_MAX_STEPS) * RISE_STEP_MS}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

interface ManagerRowProps {
  manager: Manager
  pipelines: Pipeline[]
  riseDelayMs: number
}

function ManagerRow({ manager, pipelines, riseDelayMs }: ManagerRowProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { save, isMutating } = useUpdateManager()
  const [error, setError] = useState<PipelineApiError | null>(null)
  const href = `/managers/${manager.id}`

  /** Ids of boards not in the list (archived ones) ride along untouched: the route replaces the whole list. */
  const toggle = async (pipelineId: PipelineId, on: boolean) => {
    const rest = manager.pipelineIds.filter((id) => id !== pipelineId)
    setError(null)
    try {
      await save(manager.id, { pipelineIds: on ? [...rest, pipelineId] : rest })
    } catch (cause) {
      setError(cause as PipelineApiError)
    }
  }

  // The pointer opens the page from anywhere on the row; the keyboard and
  // assistive technology get the name's own link, since a row that is a link
  // cannot also hold switches.
  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if ((event.target as HTMLElement).closest('a, button, input, select, textarea')) return
    navigate(href)
  }

  const locked = isMutating || !manager.isActive

  return (
    <TableRow
      className={`lead-row-rise group cursor-pointer${manager.isActive ? '' : ' opacity-60'}`}
      style={{ animationDelay: `${riseDelayMs}ms` }}
      onClick={handleClick}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar initials={initialsOf(manager)} seed={manager.id} aria-hidden="true" />
          <div className="flex min-w-0 flex-col gap-1">
            <Link
              to={href}
              className="truncate font-medium text-foreground outline-none hover:underline focus-visible:underline"
            >
              {fullName(manager)}
            </Link>
            <IdentityBadges manager={manager} />
          </div>
        </div>
        {error && (
          <p role="alert" className="mt-1 text-[11px] font-medium text-toast-error-text">
            {t(saveErrorKey(error))}
          </p>
        )}
      </TableCell>
      {pipelines.map((pipeline) => {
        const on = manager.pipelineIds.includes(pipeline.id)
        return (
          <TableCell
            key={pipeline.id}
            className="cursor-default text-center"
            onClick={(event) => {
              event.stopPropagation()
              if (locked || (event.target as HTMLElement).closest('button')) return
              void toggle(pipeline.id, !on)
            }}
          >
            <Switch
              checked={on}
              disabled={locked}
              onCheckedChange={(next) => void toggle(pipeline.id, next)}
              aria-label={t('pages.managers.participation.toggle', { name: pipeline.name })}
            />
          </TableCell>
        )
      })}
      <TableCell className="font-mono text-xs text-muted">
        {manager.phoneExtensions?.length ? manager.phoneExtensions.join(', ') : '—'}
      </TableCell>
      <TableCell className="text-muted">
        <ChevronRight
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-foreground"
          aria-hidden="true"
        />
      </TableCell>
    </TableRow>
  )
}
